import * as ROUTES from 'constants/routes'
import {
  Card,
  CardContent,
  Divider,
  FilledInput,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  PopoverProps,
  Stack,
  Typography,
} from '@mui/material'
import DeepcellPopover from 'components/shared/DeepcellPopover'
import DeepcellPrimaryButton from 'components/shared/DeepcellPrimaryButton'
import CopyIcon from 'components/shared/icons/CopyIcon'
import ShareIcon from 'components/shared/icons/Share'
import { useState, useMemo } from 'react'
import { useLocation } from 'react-use'
import { useRouteMatch } from 'react-router-dom'
import copy from 'copy-to-clipboard'
import { useCellVisualizationUrlParams } from '../useCellVisualizationUrlParams'

export const ShareButton = (): JSX.Element => {
  const { href, origin } = useLocation()
  const { sessionId } = useCellVisualizationUrlParams()
  const isExperimentPlanning = useRouteMatch(ROUTES.EXPERIMENT_PLANNING)
  const [el, setEl] = useState<PopoverProps['anchorEl']>(null)
  const [copiedText, setCopiedText] = useState('')

  // Generate shareable URL based on context
  const shareUrl = useMemo(() => {
    if (isExperimentPlanning) {
      // For experiment planning, include the current URL with all parameters
      return href ?? ''
    }
    
    // For cell visualizations, generate "latest version" URL
    if (!sessionId || Number.isNaN(sessionId)) {
      return href ?? ''
    }
    return `${origin}/cell-visualizations/${sessionId}`
  }, [href, origin, sessionId, isExperimentPlanning])

  const getPopoverTitle = () => {
    return isExperimentPlanning ? 'Share Experiment Configuration' : 'Share Session'
  }

  const getPopoverDescription = () => {
    return isExperimentPlanning 
      ? 'This link will share your current experiment configuration'
      : 'This link will only work for users in your organization'
  }

  const onCopy = () => {
    setCopiedText('')
    copy(shareUrl)
    setTimeout(() => setCopiedText('Copied!'), 100)
  }
  const onClose = () => {
    setCopiedText('')
    setEl(null)
  }

  return (
    <>
      <DeepcellPrimaryButton
        contained
        startIcon={<ShareIcon sx={{ fill: (t) => t.palette.primary.main }} />}
        onClick={(e) => setEl(e.currentTarget)}
      >
        Share
      </DeepcellPrimaryButton>
      <DeepcellPopover
        headerTitle={getPopoverTitle()}
        open={!!el}
        anchorEl={el}
        onClose={onClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Divider />
        <Card elevation={0}>
          <CardContent sx={{ mb: -3 }}>
            <Stack spacing={2}>
              <Typography variant="h5">Share with link</Typography>
              <FormControl variant="filled">
                <FilledInput
                  disableUnderline
                  sx={{
                    borderRadius: 2,
                    width: '450px',
                    pr: 2,
                    alignItems: 'center',
                    color: (t) => t.palette.text.disabled,
                  }}
                  inputProps={{ style: { padding: '20px' } }}
                  value={shareUrl}
                  readOnly
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton edge="end" onClick={onCopy} data-testid="copy-button">
                        <CopyIcon />
                      </IconButton>
                    </InputAdornment>
                  }
                />
                <FormHelperText sx={{ fontSize: 12, textAlign: 'right' }}>
                  {copiedText}
                </FormHelperText>
              </FormControl>
              <DeepcellPrimaryButton
                contained
                onClick={onClose}
                sx={{ width: 100, alignSelf: 'center' }}
              >
                Done
              </DeepcellPrimaryButton>
              <Typography alignSelf="center" color={(t) => t.palette.text.secondary}>
                {getPopoverDescription()}
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      </DeepcellPopover>
    </>
  )
}

export default ShareButton
